import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { User, Mail, Phone, AlertTriangle, UserPlus, Filter, ChevronLeft } from "lucide-react";
import DataTable from "../../../shared/components/DataTable";
import ProvidersSidebar from "./ProvidersSidebar.tsx";
import NewProviderModal from "./NewProviderModal";
import { useProviders } from "../hooks/useProviders";
import { Provider } from "../types/providers.types.ts";
import { Column } from "../../../shared/components/DataTable";
import { Link } from "react-router-dom";
import PageHeader from "@/shared/ui/PageHeader";
import { Button, ButtonSize } from "@/shared/ui/Button";
import CommonSearch from "@/shared/components/ui/CommonSearch";
import { useToast } from "@/shared/contexts/ToastContext";
import { WithPermission } from "@/shared/components/WithPermission";
import { PERMISSIONS } from "@/shared/constants/permissions";
import ResultCount from "@/shared/components/ui/ResultCount";
import { useRuntimeConfigContext } from "@/shared/providers/RuntimeConfigProvider";

// Maps raw API provider_type values (any casing) to human-readable display labels.
// Normalised to uppercase before lookup so Public_transport, PUBLIC_TRANSPORT, etc. all match.
const PROVIDER_TYPE_LABELS: Record<string, string> = {
  EXTERNAL_PROVIDER: "External",
  INDEPENDENT_PROVIDER: "Independent",
  NORMAL: "Normal",
  PUBLIC_TRANSPORT: "Public transport",
  RESTRICTED: "Restricted",
  "TRIPS ONLY": "Trips Only",
  TRIPS_ONLY: "Trips Only",
};

const formatProviderType = (type: string): string => {
  if (!type) return "";
  return PROVIDER_TYPE_LABELS[type.toUpperCase()] ?? type.replace(/_/g, " ");
};

const formatEdiSenderStatus = (status: string | number | null | undefined): string => {
  if (status === 0 || status === "0") {
    return "Provider";
  }

  if (status === 1 || status === "1") {
    return "Broker";
  }

  return "-";
};

/**
 * Builds a human-readable trip limit warning message from the limitType string.
 * e.g. "daily" → "The provider is over daily limit"
 */
const formatTripLimitMessage = (limitType: string): string => {
  if (!limitType) return "";
  return `The provider is over ${limitType} limit`;
};

const ProvidersPage: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { isFeatureEnabled } = useRuntimeConfigContext();
  const {
    isSidebarOpen,
    filters,
    searchTerm,
    providers,
    totalCount,
    isLoading,
    page,
    rowsPerPage,
    error,
    sortConfig,
    handleFilterChange,
    resetFilters,
    applyFilters,
    toggleSidebar,
    handleSearchChange,
    requestSort,
    isNewProviderModalOpen,
    openNewProviderModal,
    closeNewProviderModal,
    setPage,
    handleRowsPerPageChange,
    refetchProviders,
    // Saved filters functionality
    saveFilterset,
    updateFilterset,
    applySavedFilterset,
    deleteFilterset,
    savedFilters,
    isLoadingSavedFilters,
    activeSavedFilterId,
    currentFilterExpression,
    hasActiveFilters,
  } = useProviders();
  const isTripLimitEnabled = isFeatureEnabled("general.provider.tripLimit.enabled");
  const isEdiServiceEnabled = isFeatureEnabled("supervisor.feature.ediservice");

  const handleProviderCreated = () => {
    refetchProviders();
    showToast(t("providers.toast.createSuccess", "Provider created successfully"), "success");
  };

  const breadcrumbs = useMemo(
    () => [
      { label: t("breadcrumbs.administration"), url: "/administration" },
      { label: t("providers.title") },
    ],
    [t]
  );

  const pageActions = useMemo(
    () => (
      <div className="flex items-center space-x-4">
        <WithPermission permission={PERMISSIONS.COMPANY_CREATE}>
          <Button
            size={ButtonSize.Xs}
            onClick={openNewProviderModal}
            data-testid="new-provider-button"
            className="flex items-center"
          >
            <UserPlus size={16} className="mr-2" />
            {t("providers.newProvider")}
          </Button>
        </WithPermission>
      </div>
    ),
    [t, openNewProviderModal]
  );

  const providerColumns: Column<Provider>[] = useMemo(() => {
    const columns: Column<Provider>[] = [
      {
        headerName: t("providers.columns.providerName"),
        field: "name",
        sortable: true,
        sticky: true,
        stickyLeft: "0",
        showRightShadow: true,
        cell: (row: Provider) => (
          <Link
            to={`/admin/provider/${row.id}`}
            className="text-tableBlueColor hover:underline whitespace-nowrap flex items-center gap-2"
          >
            <span>{row.name || 'N/A'}</span>
            {isTripLimitEnabled && row.tripLimitStatus?.isOverLimit && (
              <div className="relative group">
                <AlertTriangle className="text-warning h-4 w-4" />
                <div className="absolute left-[40px] -translate-x-1/2 bottom-full mb-2 hidden group-hover:block z-50 w-max max-w-xs">
                  <div className="bg-warning text-on-semantic text-xs rounded py-2 px-3 shadow-lg">
                    {row.tripLimitStatus.warningMessage}
                  </div>
                </div>
              </div>
            )}
          </Link>
        ),
      },
      {
        headerName: t("providers.columns.type"),
        field: "provider_type",
        sortable: true,
        cellClassName: "py-3 whitespace-nowrap",
        cell: (row: Provider) => <span>{formatProviderType(row.provider_type)}</span>,
      },
      {
        headerName: t("providers.columns.priority"),
        field: "priority",
        sortable: true,
        cellClassName: "py-3",
        cell: (row: Provider) => {
          if (!row.priority) return <span>-</span>;
          const priorityColors: Record<string, { bg: string; border: string; text: string }> = {
            "Very high": { bg: "#FFE4E5", border: "#F7B1B4", text: "#343434" },
            High: { bg: "#FAEEE5", border: "#F8C6A0", text: "#343434" },
            "Above average": { bg: "#FFFCD2", border: "#F2D1B1", text: "#343434" },
            Medium: { bg: "#F2F2F2", border: "#D0D0D0", text: "#343434" },
            Low: { bg: "#ECF4FE", border: "#C3DBFC", text: "#343434" },
            "Very low": { bg: "#F5E0FF", border: "#C7A4E5", text: "#343434" },
            "Below Average": { bg: "#D3FFEF", border: "#A8E6CF", text: "#343434" },
          };
          const colors = priorityColors[row.priority] ?? {
            bg: "#F2F2F2",
            border: "#D0D0D0",
            text: "#343434",
          };
          return (
            <span
              className="inline-flex items-center rounded-[4px] border px-[8px] py-[3px] text-[12px] font-medium whitespace-nowrap"
              style={{ backgroundColor: colors.bg, borderColor: colors.border, color: colors.text }}
            >
              {row.priority}
            </span>
          );
        },
      },
      {
        headerName: t("providers.columns.subcontractor"),
        field: "transportation_subcontractor",
        sortable: true,
        cellClassName: "py-3",
        cell: (row: Provider) => (
          <span>
            {row.transportation_subcontractor ? t("common.yes", "Yes") : t("common.no", "No")}
          </span>
        ),
      },
    ];

    if (isEdiServiceEnabled) {
      columns.push({
        headerName: t("providers.columns.edi"),
        field: "edi_sender_status",
        sortable: true,
        cellClassName: "py-3",
        cell: (row: Provider) => <span>{formatEdiSenderStatus(row.edi_sender_status)}</span>,
      });
    }

    if (isTripLimitEnabled) {
      columns.push({
        headerName: t("providers.tripLimitStatus"),
        field: "tripLimitStatus",
        sortable: false,
        cellClassName: "py-3",
        cell: (row: Provider) => {
          const status = row.tripLimitStatus;
          if (!status?.isOverLimit || !status.limitType) return <span></span>;
          const displayMessage = formatTripLimitMessage(status.limitType);
          return <span className="text-red-600 text-sm">{displayMessage}</span>;
        },
      });
    }

    columns.push(
      {
        headerName: t("providers.columns.code"),
        field: "code",
        sortable: true,
        cellClassName: "py-3 whitespace-nowrap",
        cell: (row: Provider) => <span>{row.code ?? "-"}</span>,
      },
      {
        headerName: t("providers.columns.contact"),
        field: "contact_name",
        sortable: true,
        cellClassName: "py-3",
        cell: (row: Provider) => (
          <div className="flex flex-col gap-1 py-2">
            {row.contact_name && (
              <div className="flex items-center gap-2">
                <User className="h-3.5 w-3.5 text-text-muted/60 flex-shrink-0" />
                <span className="text-sm text-text">{row.contact_name}</span>
              </div>
            )}
            {row.contact_address && (
              <div className="flex items-start gap-2">
                <span className="h-3.5 w-3.5 flex-shrink-0 text-text-muted/60 text-xs leading-[14px] mt-[1px]">
                  #
                </span>
                <span className="text-sm text-text">{row.contact_address}</span>
              </div>
            )}
            {(row.contact_phone1 || row.contact_phone2) && (
              <div className="flex items-center gap-2">
                <Phone className="h-3.5 w-3.5 text-text-muted/60 flex-shrink-0" />
                <span className="text-sm text-text">
                  {row.contact_phone1}
                  {row.contact_phone1 && row.contact_phone2 && " / "}
                  {row.contact_phone2}
                </span>
              </div>
            )}
            {row.contact_email && (
              <div className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-text-muted/60 flex-shrink-0" />
                <span className="text-sm text-text">{row.contact_email}</span>
              </div>
            )}
          </div>
        ),
      }
    );

    return columns;
  }, [isEdiServiceEnabled, isTripLimitEnabled, t]);

  return (
    <div className="bg-surface-secondary h-[90vh]" data-testid="providers-page-container">
      <div className="flex h-[92vh]">
        <div
          className={`transition-all duration-300 flex-shrink-0 overflow-hidden border-r border-sidebarHeaderBorder ${
            isSidebarOpen ? "w-[319px]" : "w-20"
          }`}
          data-testid="providers-sidebar-wrapper"
        >
          {isSidebarOpen ? (
            <ProvidersSidebar
              filters={filters}
              onFilterChange={handleFilterChange}
              onReset={resetFilters}
              onApply={applyFilters}
              onSave={saveFilterset}
              savedFilters={savedFilters}
              onApplyFilterset={applySavedFilterset}
              onUpdateFilterset={updateFilterset}
              onDeleteFilterset={deleteFilterset}
              isLoadingSavedFilters={isLoadingSavedFilters}
              activeSavedFilterId={activeSavedFilterId}
              currentFilterExpression={currentFilterExpression}
            />
          ) : (
            <>
              <div className="p-4 flex flex-col items-center">
                <button
                  onClick={toggleSidebar}
                  className="p-2 focus:outline-none bg-transparent hover:bg-transparent"
                  aria-label={t("providers.openFiltersSidebar")}
                  data-testid="open-filters-sidebar-button"
                >
                  <Filter className="text-primary w-4 h-4" />
                </button>
              </div>

              <div className="border-b border-border"></div>
            </>
          )}
        </div>

        <div className="flex flex-col flex-1 relative min-w-0">
          <button
            onClick={toggleSidebar}
            className="text-arrowIcon absolute top-[83px] -left-3 transform -translate-y-1/2 border border-border rounded-full p-[3px] shadow-[0px_5px_8px_-4px_rgba(0,0,0,0.54),0_4px_6px_-4px_rgba(0,0,0,0.1)] hover:border-border focus:outline-none h-6 w-6 flex items-center justify-center bg-surface"
            style={{ zIndex: 50 }}
            aria-label={isSidebarOpen ? t("providers.closeSidebar") : t("providers.openSidebar")}
            data-testid="toggle-sidebar-button"
          >
            <ChevronLeft
              className={`w-5 h-5 transition-transform ${!isSidebarOpen ? "rotate-180" : ""}`}
            />
          </button>

          <main className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-shrink-0">
              <PageHeader
                title={t("providers.title")}
                subtitle={t("providers.subtitle")}
                breadcrumbs={breadcrumbs}
                actions={pageActions}
              />

              <div className="px-8 pt-5 pb-5">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="w-full sm:w-auto sm:min-w-[20rem] sm:max-w-[438px]">
                    <CommonSearch
                      placeholder={t("providers.searchPlaceholder")}
                      value={searchTerm}
                      onChange={handleSearchChange}
                      data-testid="providers-search-input"
                      aria-label={t("providers.searchPlaceholder")}
                      containerClassName="w-full"
                    />
                  </div>
                  <ResultCount
                    total={totalCount}
                    isFiltered={Boolean(searchTerm.trim()) || hasActiveFilters}
                    entity={t("providers.title", "providers").toLowerCase()}
                  />
                </div>
              </div>
            </div>

            <div className="px-4 md:px-6 pt-1 pb-2 overflow-y-auto">
              <DataTable
                data-testid="providers-data-table"
                data={providers}
                columns={providerColumns}
                sortConfig={sortConfig}
                requestSort={requestSort}
                serverSideSorting={true}
                isLoading={isLoading}
                error={error}
                totalCount={totalCount}
                page={page}
                onPageChange={setPage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => handleRowsPerPageChange(Number(e.target.value))}
              />
            </div>
          </main>
        </div>
      </div>
      <NewProviderModal
        isOpen={isNewProviderModalOpen}
        onClose={closeNewProviderModal}
        onProviderCreated={handleProviderCreated}
      />
    </div>
  );
};

export default ProvidersPage;
